// Copyright ©️ 2026 eVolve MEP, LLC

import type { HubData, ProjectData } from '@aps_sdk/data-management';
import type {
  EncodedJwt,
  AutodeskToken,
  CustomProperty,
  Facility,
  ProductId,
  PriorityId,
  MessagePriority,
  ISOTimestamp,
  TimeFrameData,
  PartCatalog,
  Project,
  WorkCellId,
  Username,
  UserId,
  TableView,
  TableViewType,
  TableViewWithGridState,
  CustomPropertyTypeName,
  WorkOrder,
  EvolveApiResponse,
  VDCOSChartToolType,
} from '@evolvemep/api-ts';
import type { WorkOrderStateName } from '@evolvemep/api-ts/src';

import type { SpoolingSettings } from 'components/Autodesk/extensions/SpoolsExtension/types';
import type { SelectedView } from 'components/Mantine/ChangeDataView/useChangeDataView';
import type { GlobalFilterValues } from 'components/Mantine/GlobalFilters/types';
import type { PageTitle } from 'components/Mantine/Navigation/types';
import type {
  UserPreferenceKey,
  UserPreferenceValue,
} from 'hooks/typeCacheHooks/useUserPreferences/useUserPreferences';
import type { ValidCacheableRoute } from 'hooks/useEvolveGetWithLocalStorage';
import type { FabricationTab } from 'modules/Shop/EvolveFabrication/EvolveFabricationPage';
import type { VDCOSChartOptionalFilters } from 'modules/Shop/Insights/RevitUsage/useChartFilters';
import type { ViewingAttachment } from 'modules/Shop/Tasks/TaskViewer/FullScreenAttachmentViewer';

export const MESSAGE_PRIORITIES = {
  HIGH: 'e3ee36f5-fbb6-4886-a63e-43ae04c14877' as PriorityId,
  NORMAL: '582634fe-763b-4364-acc1-fbbf5b7e387b' as PriorityId,
} as const satisfies Record<MessagePriority, PriorityId>;

export const PRODUCT_IDS = {
  MECHANICAL_LICENSE: 'fdb6375d-09ee-11ed-82eb-1296f7ac1dd9' as ProductId,
  ELECTRICAL_LICENSE: 'fdb63ec7-09ee-11ed-82eb-1296f7ac1dd9' as ProductId,
  SHOP_LICENSE: 'fec5765a-8c00-401f-82cb-e1d1b37dd3d6' as ProductId,
  SYNC_DESKTOP_LICENSE: 'be2a158a-0b36-4f55-a2a8-524236737479' as ProductId,
  FABRICATION_LICENSE: '84251586-3883-40d8-aa6c-fc5cdaa559d6' as ProductId,
  WEB_SPOOLING_LICENSE: 'd9362d9a-d9dd-11f0-b83b-0224e7848a91' as ProductId,
  REVIT_ROLES: 'd9ac7d91-9f27-11f0-a6d3-0e1a10b7a936' as ProductId,
  REVIT_USAGE_HISTORY: '42bd1ad7-de16-4ee6-a422-c3cf9c594c57' as ProductId,
  MATERIAL_ANALYTICS: 'ecc218cb-9f27-11f0-a6d3-0e1a10b7a938' as ProductId,
} as const satisfies Record<string, ProductId>;

export type LOCAL_STORAGE_TYPE = {
  EVOLVE_ACCESS_TOKEN: EncodedJwt;
  EVOLVE_REFRESH_TOKEN: EncodedJwt;
  EVOLVE_TOKEN_USERNAME: Username;
  STAY_SIGNED_IN: 'true' | null;
  SELECTED_CATALOG: PartCatalog | null;
  SELECTED_WORK_CELL: WorkCellId | null;
  TIME_TRACKING_FILTERS: TimeFrameData;
  INSIGHTS_DETAILED: boolean;
  AUTODESK_AUTH: AutodeskToken | undefined;
  AUTODESK_HUB: NonNullable<HubData['id']> | null;
  AUTODESK_PROJECT: NonNullable<ProjectData['id']> | null;
  AUTODESK_AUTH_REJECTED: boolean;
  GLOBAL_FILTERS: GlobalFilterValues;
  LAST_VIEWED_PROJECT: Project | null;
  LAST_VIEWED_FACILITY: Facility | null;
  SPOOLING_SETTINGS: SpoolingSettings;
  VDC_OS_ANALYTICS_FILTERS: VDCOSChartOptionalFilters;
  VDC_OS_TOOL_TYPE: VDCOSChartToolType;
  FABRICATION_TAB: FabricationTab;
  FABRICATION_GROUP_BY: 'none' | 'cut-lists';
  VIEWING_ATTACHMENT: ViewingAttachment | undefined;
  PENDING_STATUS_RETIREMENT_BANNER_HIDDEN: boolean;
  // Computed keys
  [key: `TABLE_VIEWS_${TableViewType}`]: TableView[];
  [key: `DEFAULT_TABLE_VIEW_${TableViewType}`]: TableViewWithGridState | null;
  [key: `${UserId}-${WorkOrder['identifier']}-${WorkOrderStateName}-accepted`]: ISOTimestamp | null;
} & {
  [K in PageTitle as `DATA_VIEW_${K}`]: SelectedView;
} & {
  [K in UserPreferenceKey as `USER_PREFERENCE_${K}`]: UserPreferenceValue<K> | null;
} & {
  [K in CustomPropertyTypeName as `${K}_CUSTOM_PROPERTIES`]: CustomProperty[] | undefined;
} & {
  [K in ValidCacheableRoute as `EVOLVE_GET_CACHE_${K}_${string}`]: EvolveApiResponse<'GET', K> | undefined;
};

export type LOCAL_STORAGE_KEY = keyof LOCAL_STORAGE_TYPE;

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000' as const;
